import type { AcaoDeWaf } from '@/api/waf'

/** Espelho de `waf_codigo/acoes.py`: os gestos que o módulo waf expressa (spec D4). */
export const GRUPOS: Record<string, string> = {
  common_rule_set: 'CommonRuleSet',
  known_bad_inputs: 'KnownBadInputs',
  amazon_ip_reputation: 'AmazonIpReputationList',
  sqli: 'SQLi',
  anti_ddos: 'AntiDDoSRuleSet',
  admin_protection: 'AdminProtectionRuleSet',
  linux: 'LinuxRuleSet',
}

const SO_NO_PADRAO = new Set(['anti_ddos', 'admin_protection', 'linux'])

/**
 * Os grupos que existem no conjunto do stage (espelho de `traducao.conjunto`):
 * o legado tem quatro; o standard-2026-10 tem seis, sete com o anti-DDoS ligado.
 */
export function gruposDoConjunto(
  campos: Record<string, { valor: unknown } | undefined>,
): Record<string, string> {
  const padrao = campos.rule_set?.valor === 'standard-2026-10'
  const antiDdos = campos.enable_anti_ddos?.valor === true
  return Object.fromEntries(Object.entries(GRUPOS).filter(([g]) => {
    if (!SO_NO_PADRAO.has(g)) return true
    return padrao && (g !== 'anti_ddos' || antiDdos)
  }).map(([g, nome]) => [g, padrao && g === 'sqli' ? 'SQLiRuleSet' : nome]))
}

export const TIPOS: { tipo: string; rotulo: string; campo: string }[] = [
  { tipo: 'grupo_block', rotulo: 'Promover grupo gerenciado para block', campo: 'managed_rules_action' },
  { tipo: 'grupo_count', rotulo: 'Rebaixar grupo gerenciado para count', campo: 'managed_rules_action' },
  { tipo: 'excecao', rotulo: 'Exceção: uma sub-regra só conta', campo: 'managed_rule_overrides' },
  { tipo: 'tirar_excecao', rotulo: 'Tirar a exceção de uma sub-regra', campo: 'managed_rule_overrides' },
  { tipo: 'geo_acao', rotulo: 'Ação do bloqueio por país', campo: 'geo_action' },
  { tipo: 'geo_paises', rotulo: 'Países permitidos', campo: 'geo_allowed_countries' },
  { tipo: 'rate_acao', rotulo: 'Ação do rate limit', campo: 'rate_limit_action' },
  { tipo: 'rate_limite', rotulo: 'Limite do rate (requisições por 5 min)', campo: 'rate_limit' },
  { tipo: 'liberar_ip', rotulo: 'Liberar IP na AllowList', campo: 'allowed_ips' },
  { tipo: 'tirar_ip', rotulo: 'Tirar IP da AllowList', campo: 'allowed_ips' },
  { tipo: 'adotar_aws', rotulo: 'Trazer o código para o estado da AWS', campo: '' },
]

export function descreverAcao(a: AcaoDeWaf): string {
  const regra = GRUPOS[a.grupo ?? ''] ?? a.grupo ?? ''
  const v = a.valor
  switch (a.tipo) {
    case 'grupo_block': return `Promover ${regra} para block`
    case 'grupo_count': return `Rebaixar ${regra} para count`
    case 'excecao': return `Exceção em ${regra}: ${a.sub_regra} só conta`
    case 'tirar_excecao': return `Tirar a exceção de ${a.sub_regra} em ${regra}`
    case 'geo_acao': return `Geo em ${v}`
    case 'geo_paises': return `Países permitidos: ${Array.isArray(v) ? v.join(', ') : ''}`
    case 'rate_acao': return `Rate limit em ${v}`
    case 'rate_limite': return `Rate limit de ${v} requisições por 5 min`
    case 'liberar_ip': return `Liberar ${v} na AllowList`
    case 'tirar_ip': return `Tirar ${v} da AllowList`
    case 'adotar_aws': return 'Trazer o código para o estado da AWS'
    case 'adotar_no_codigo': return 'Trazer este WAF para o código do stage'
    default: return a.tipo
  }
}
